// La hoja de Mezclar (spec §3.5, ADR-2026-09-17-mezclar-es-un-modo-del-lienzo): SIN fondo,
// porque sumar partes es tocar celdas del lienzo. Lista las partes (la instrucción de
// cada una se escribe al lado de su foto, en el lienzo), la instrucción adicional
// PLEGADA porque es opcional, y en el pie fijo la cantidad y Generar
// (ADR-2026-09-17-las-hojas-del-diseno-eligen-la-cantidad). Cerrarla apaga la mezcla:
// no se guarda (ADR-2026-09-17-la-mezcla-no-se-guarda).

import { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import CachedImg from '../../../../../components/CachedImg';
import { CreditBadge } from '../../../../../components/CreditBadge';
import { HojaLateral } from '../../../../../chat-atlas/ui/HojaLateral';
import { ContadorImagenes } from '../../../components/ContadorImagenes';
import { useDiseno } from '../disenoContext';
import { MAX_CANTIDAD, MAX_PARTES, TOPE_INSTRUCCION } from '../promptsDeDiseno';

export const PanelMezclar: React.FC<{ baseId: string }> = ({ baseId }) => {
  const d = useDiseno();
  const mezcla = d.mezcla;
  const partes = mezcla?.partes ?? [];
  const general = mezcla?.instruccion ?? '';
  const cantidad = mezcla?.cantidad ?? 1;
  // Plegada salvo que ya tenga texto (la armó el agente, o se volvió a abrir).
  const [instruccionAbierta, setInstruccionAbierta] = useState(general.length > 0);

  const cerrar = () => {
    d.setParteEnEdicion(null);
    d.setMezcla(() => null);
    d.abrirPanel(null);
  };
  const urlBase = d.urlDe(baseId);

  const pie = (
    <div className="flex items-center gap-3">
      <div className="shrink-0 rounded-xl bg-black/[0.035] px-2 py-1 [&>span]:mt-0">
        <ContadorImagenes valor={cantidad} max={MAX_CANTIDAD} onChange={(v) => d.setMezcla((m) => (m ? { ...m, cantidad: v } : m))} />
      </div>
      <div className="flex flex-1 justify-end gap-2">
        <button type="button" onClick={cerrar} className="px-3 py-2 text-[15px] text-[#3C3C43]/60 transition-colors hover:text-[rgb(var(--brand-black-rgb)_/_0.8)]">
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => d.generarMezcla()}
          disabled={partes.length === 0}
          className="inline-flex items-center gap-2 rounded-md bg-brand-primary px-4 py-2 text-[15px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-60"
        >
          Generar
          <CreditBadge credits={cantidad} tone="on-primary" size="sm" />
        </button>
      </div>
    </div>
  );

  return (
    <HojaLateral abierta sinFondo titulo="Mezclar" bajada={`Tocá texturas, inspiraciones u otras prendas del lienzo para sumarlas (hasta ${MAX_PARTES}) y escribí al lado de cada una qué tomar. Con el Marcador de la barra pintás dónde va el cambio en la base, o qué tomar de una parte.`} onCerrar={cerrar} pie={pie}>
      <div className="flex flex-col gap-4 px-2 py-1">
        <div className="flex items-center gap-3">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[#F3F4F8]">
            {urlBase && <CachedImg src={urlBase} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="min-w-0">
            <p className="truncate text-[14px] font-semibold text-brand-black">{d.tituloDe(baseId)}</p>
            <p className="text-[12.5px] text-[#3C3C43]/60">
              La prenda base: se conserva su silueta y su construcción.
              {mezcla?.marcaBase && <span className="ml-1 font-semibold text-[#FF2D2D]">Zona pintada: ahí va el cambio.</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-normal uppercase tracking-wide text-[#3C3C43]/60">Partes · {partes.length}</span>
          {partes.length === 0 && (
            <p className="rounded-xl border border-dashed border-black/[0.12] px-3 py-4 text-center text-[13px] text-[#3C3C43]/60">
              Todavía no hay partes. Tocá una imagen del lienzo.
            </p>
          )}
          {partes.map((p, i) => {
            const url = d.urlDe(p.celdaId);
            return (
              <div key={p.celdaId} className="flex items-center gap-2 rounded-xl border border-black/[0.06] bg-white p-2">
                <button type="button" onClick={() => d.setParteEnEdicion(p.celdaId)} className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-[#F3F4F8]" title="Editar qué tomar">
                  {url && <CachedImg src={url} alt="" className="h-full w-full object-cover" />}
                  <span className="absolute left-1 top-1 rounded-full bg-brand-primary px-1.5 text-[10px] font-semibold text-white">{i + 2}</span>
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-semibold text-brand-black">{d.tituloDe(p.celdaId) || 'Imagen'}</p>
                  <p className="truncate text-[12.5px] text-[#3C3C43]/70">
                    {p.instruccion.trim() || 'Tomar lo que la caracteriza'}
                    {p.marca && <span className="ml-1 font-semibold text-[#FF2D2D]">· zona pintada</span>}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => d.alternarParte(p.celdaId)}
                  aria-label="Sacar de la mezcla"
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black/[0.06] text-[rgb(var(--brand-black-rgb)_/_0.5)] hover:bg-black/[0.1]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => setInstruccionAbierta((v) => !v)}
            aria-expanded={instruccionAbierta}
            className="flex items-center justify-between rounded-lg py-1 text-left text-[13px] font-normal uppercase tracking-wide text-[#3C3C43]/60 transition-colors hover:text-brand-black"
          >
            <span>
              Instrucción opcional
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${instruccionAbierta ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          {instruccionAbierta && (
            <textarea
              autoFocus
              value={general}
              onChange={(e) => {
                const texto = e.target.value.slice(0, TOPE_INSTRUCCION);
                d.setMezcla((m) => (m ? { ...m, instruccion: texto } : m));
              }}
              placeholder='Ej.: "más veraniego", "para una colección de invierno"'
              rows={3}
              className="w-full resize-none rounded-xl border border-black/[0.08] bg-white px-3 py-2 text-[14px] text-brand-black outline-none focus:border-brand-primary"
            />
          )}
        </div>
      </div>
    </HojaLateral>
  );
};
